import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errorToMessage } from "@/lib/apiClient";
import Modal, { ConfirmDialog } from "@/components/Modal";
import {
  EmptyState, ErrorMessage, PageHeader, Pagination, SearchInput, StatusBadge, StatusFilter, Toolbar,
} from "@/components/DataTableParts";
import { Plus, Pencil, Trash2, Loader2, Lock } from "lucide-react";

const ENDPOINT = "/telephony-providers";
const ADAPTERS = ["MANUAL", "RETELL", "VAPI", "ELEVENLABS", "EXOTEL", "TWILIO"];

const emptyForm = {
  code: "",
  label: "",
  adapter_key: "MANUAL",
  is_active: true,
  priority: 100,
  config_json: "{}",
  credentials_json: "",
  webhook_secret: "",
};

export default function TelephonyProvidersPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [isActive, setIsActive] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const [qDebounced, setQDebounced] = useState(q);
  useEffect(() => { const t = setTimeout(() => setQDebounced(q), 300); return () => clearTimeout(t); }, [q]);

  const params = useMemo(() => {
    const p = { page, page_size: 10 };
    if (qDebounced) p.q = qDebounced;
    if (isActive !== null) p.is_active = isActive;
    return p;
  }, [page, qDebounced, isActive]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get(ENDPOINT, { params })).data); }
    catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("TelephonyProvidersPage.load failed", err?.message || err);
      setData({ items: [], meta: null });
    }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setFormError(""); setShowForm(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      code: row.code,
      label: row.label,
      adapter_key: row.adapter_key,
      is_active: row.is_active,
      priority: row.priority,
      config_json: JSON.stringify(row.config || {}, null, 2),
      credentials_json: "",
      webhook_secret: "",
    });
    setFormError(""); setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    setSubmitting(true);
    try {
      let config;
      try { config = JSON.parse(form.config_json || "{}"); }
      catch { throw new Error("Config must be valid JSON."); }

      let credentials = null;
      if (form.credentials_json?.trim()) {
        try { credentials = JSON.parse(form.credentials_json); }
        catch { throw new Error("Credentials must be valid JSON."); }
      }

      const payload = {
        code: form.code,
        label: form.label,
        adapter_key: form.adapter_key,
        is_active: form.is_active,
        priority: Number(form.priority),
        config,
      };
      if (credentials !== null) payload.credentials = credentials;
      if (form.webhook_secret) payload.webhook_secret = form.webhook_secret;

      if (editing) {
        const { code, ...upd } = payload;
        await api.patch(`${ENDPOINT}/${editing.id}`, upd);
      } else {
        await api.post(ENDPOINT, payload);
      }
      setShowForm(false);
      await load();
    } catch (err) { setFormError(errorToMessage(err)); }
    finally { setSubmitting(false); }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    await api.delete(`${ENDPOINT}/${toDelete.id}`);
    await load();
  };

  return (
    <div>
      <PageHeader
        title="Telephony Providers"
        subtitle="Provider-independent adapter config. Credentials encrypted at rest and never returned by the API."
        actions={
          <button onClick={openCreate} data-testid="btn-new-provider" className="inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-sm">
            <Plus size={16} /> New Provider
          </button>
        }
      />
      <Toolbar>
        <SearchInput value={q} onChange={setQ} placeholder="Search by code or label…" />
        <StatusFilter value={isActive} onChange={setIsActive} />
      </Toolbar>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="providers-table">
          <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Code</th>
              <th className="text-left px-4 py-2 font-medium">Label</th>
              <th className="text-left px-4 py-2 font-medium">Adapter</th>
              <th className="text-right px-4 py-2 font-medium">Priority</th>
              <th className="text-left px-4 py-2 font-medium">Credentials</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500"><Loader2 className="inline animate-spin" size={16}/> Loading…</td></tr>
            ) : data?.items?.length ? (
              data.items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono">{r.code}</td>
                  <td className="px-4 py-2">{r.label}</td>
                  <td className="px-4 py-2 text-slate-600">{r.adapter_key}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.priority}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center gap-1 text-xs ${r.has_credentials ? "text-emerald-700" : "text-slate-500"}`}>
                      <Lock size={12} /> {r.has_credentials ? "Set" : "Not set"}
                    </span>
                  </td>
                  <td className="px-4 py-2"><StatusBadge active={r.is_active} /></td>
                  <td className="px-4 py-2 text-right">
                    <button data-testid={`edit-${r.code}`} onClick={() => openEdit(r)} className="text-slate-600 hover:text-slate-900 mr-2"><Pencil size={16} /></button>
                    <button data-testid={`delete-${r.code}`} onClick={() => setToDelete(r)} className="text-rose-600 hover:text-rose-800"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={7}><EmptyState label="No telephony providers configured yet." /></td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination meta={data?.meta} page={page} onPage={setPage} />

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? `Edit ${editing.code}` : "New Telephony Provider"} size="lg">
        <form onSubmit={submit} className="space-y-3" data-testid="provider-form">
          <ErrorMessage message={formError} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code (UPPER_SNAKE)"><input required disabled={!!editing} className="input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} pattern="^[A-Z0-9_]+$" data-testid="field-code" /></Field>
            <Field label="Label"><input required className="input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="field-label" /></Field>
            <Field label="Adapter">
              <select className="input" value={form.adapter_key} onChange={(e) => setForm({ ...form, adapter_key: e.target.value })} data-testid="field-adapter">
                {ADAPTERS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </Field>
            <Field label="Priority (lower = higher preference)"><input type="number" min={0} max={10000} className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="field-priority" /></Field>
          </div>
          <Field label="Config (JSON) — provider-specific non-secret settings">
            <textarea rows={4} className="input font-mono text-xs" value={form.config_json} onChange={(e) => setForm({ ...form, config_json: e.target.value })} data-testid="field-config" />
          </Field>
          <Field label={`Credentials (JSON)${editing ? " — leave empty to keep existing" : ""}`}>
            <textarea rows={3} className="input font-mono text-xs" placeholder='{"api_key":"..."}' value={form.credentials_json} onChange={(e) => setForm({ ...form, credentials_json: e.target.value })} data-testid="field-credentials" />
          </Field>
          <Field label={`Webhook Secret${editing ? " — leave empty to keep existing" : ""}`}>
            <input type="password" className="input" value={form.webhook_secret} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })} data-testid="field-webhook-secret" />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="field-active"/> Active
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 text-sm rounded-md border border-slate-300">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="provider-submit" className="px-3 py-1.5 text-sm rounded-md bg-slate-900 text-white disabled:opacity-60">
              {submitting ? "Saving…" : editing ? "Save Changes" : "Create Provider"}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog open={!!toDelete} onClose={() => setToDelete(null)} onConfirm={doDelete}
        title="Delete provider?" message={`This will soft-delete "${toDelete?.code}".`} confirmLabel="Delete" />
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-xs text-slate-600 mb-1">{label}</label>{children}</div>;
}
